import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { SwUpdate } from '@angular/service-worker';
import { ActivatedRoute, NavigationEnd, NavigationError, Router } from '@angular/router';

@Component({
    selector: 'app-update-popup',
    templateUrl: './update-popup.component.html',
    styleUrls: ['./update-popup.component.scss'],
})
export class UpdatePopupComponent implements OnInit {
    showPopup = false;
    private readonly staleChunkRecoveryKey = 'platfio:stale-chunk-recovery-attempted';

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: Object,
        private updates: SwUpdate,
        private router: Router,
        private route: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        this.applyForceParam();
        this.checkForUpdates();
        this.recoverFromStaleChunks();
    }

    private applyForceParam(): void {
        // Allow forcing the popup via query param for testing: ?forceUpdatePopup=true
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        // Get query params from current URL since ActivatedRoute might not work at app level
        const url = new URL(this.document.location.href);
        const params = url.searchParams;

        const raw = params.get('forceUpdatePopup') || params.get('updatePopup') || params.get('showUpdatePopup');

        if (!raw) {
            return;
        }

        const val = String(raw).toLowerCase();

        if (val === '1' || val === 'true' || val === 'yes' || val === 'y') {
            this.showPopup = true;
        }
    }

    private checkForUpdates(): void {
        if (!isPlatformBrowser(this.platformId)) return;
        const window = this.document.defaultView;
        if (window?.location.hostname === 'localhost' || !this.updates.isEnabled) {
            return;
        }

        this.updates.versionUpdates.subscribe((event) => {
            if (event.type === 'VERSION_READY' && !this.router.url.startsWith('/landing')) {
                this.showPopup = true;
            }
        });

        this.updates.unrecoverable.subscribe(() => {
            this.reloadOnceForStaleChunks();
        });
    }

    private recoverFromStaleChunks(): void {
        if (!isPlatformBrowser(this.platformId)) return;

        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.clearStaleChunkRecoveryAttempt();
                return;
            }

            if (!(event instanceof NavigationError) || !this.isStaleChunkError(event.error)) {
                return;
            }

            this.reloadOnceForStaleChunks();
        });
    }

    private reloadOnceForStaleChunks(): void {
        if (this.hasAttemptedStaleChunkRecovery()) {
            this.showPopup = true;
            return;
        }

        if (!this.canPersistStaleChunkRecoveryAttempt()) {
            this.showPopup = true;
            return;
        }

        this.markStaleChunkRecoveryAttempted();
        this.document.location.reload();
    }

    private isStaleChunkError(error: unknown): boolean {
        const message = this.getErrorMessage(error);

        return message.includes('Failed to fetch dynamically imported module')
            || message.includes('Importing a module script failed')
            || message.includes('Expected a JavaScript-or-Wasm module script')
            || message.includes('ChunkLoadError')
            || message.includes('Loading chunk');
    }

    private getErrorMessage(error: unknown): string {
        if (error instanceof Error) {
            return `${error.name} ${error.message}`;
        }

        if (typeof error === 'string') {
            return error;
        }

        try {
            return JSON.stringify(error);
        } catch {
            return '';
        }
    }

    private hasAttemptedStaleChunkRecovery(): boolean {
        try {
            return this.document.defaultView?.sessionStorage.getItem(this.staleChunkRecoveryKey) === 'true';
        } catch {
            return false;
        }
    }

    private canPersistStaleChunkRecoveryAttempt(): boolean {
        try {
            const storage = this.document.defaultView?.sessionStorage;
            if (!storage) {
                return false;
            }

            storage.setItem(this.staleChunkRecoveryKey, storage.getItem(this.staleChunkRecoveryKey) || '');
            return true;
        } catch {
            return false;
        }
    }

    private markStaleChunkRecoveryAttempted(): void {
        try {
            this.document.defaultView?.sessionStorage.setItem(this.staleChunkRecoveryKey, 'true');
        } catch {
            // Guarded by canPersistStaleChunkRecoveryAttempt.
        }
    }

    private clearStaleChunkRecoveryAttempt(): void {
        try {
            this.document.defaultView?.sessionStorage.removeItem(this.staleChunkRecoveryKey);
        } catch {
            // A failed clear only means the next stale chunk error shows the refresh prompt.
        }
    }

    refreshPage(): void {
        this.document.location.reload();
    }

    closePopup(): void {
        this.showPopup = false;
    }
}
